import { Settings as SettingsIcon, FileCode } from "lucide-react";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/ui/EmptyState";
import { RecommendationSummaryBar } from "../../components/recommendations/RecommendationSummaryBar";
import { RecommendationTable } from "../../components/recommendations/RecommendationTable";
import { RecommendationSummary, RecommendationSection } from "@/types/recommendation";

interface Workspace {
  name: string;
  slug: string;
}

interface GithubRepositoryShowProps {
  workspace: Workspace;
  github_repository: {
    uuid: string;
    owner: string;
    name: string;
    created_at: string;
  };
  recommendation_summary: RecommendationSummary;
  recommendation_sections: RecommendationSection[];
  has_terraform_states: boolean;
  can_edit: boolean;
  can_delete: boolean;
  organization_prs_remaining: number;
  organization_plan: string;
}

export default function GithubRepositoryShow({
  workspace,
  github_repository,
  recommendation_summary,
  recommendation_sections,
  has_terraform_states,
  can_edit,
  organization_prs_remaining,
  organization_plan,
}: GithubRepositoryShowProps) {
  const breadcrumbs = [
    { label: "Workspaces", href: "/workspaces" },
    { label: workspace.name, href: `/workspaces/${workspace.slug}` },
    { label: `${github_repository.owner}/${github_repository.name}` },
  ];

  return (
    <Layout title={`${github_repository.owner}/${github_repository.name}`}>
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={breadcrumbs} />

          <PageHeader
            title={`${github_repository.owner}/${github_repository.name}`}
            actions={
              can_edit && (
                <a
                  href={`/workspaces/${workspace.slug}/github_repositories/${github_repository.uuid}/edit`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  <SettingsIcon className="w-4 h-4" />
                  Settings
                </a>
              )
            }
          />

          {!has_terraform_states ? (
            <EmptyState
              icon={FileCode}
              title="No infrastructure states connected"
              description="Infrastructure states are created and managed from the cloud account settings pages."
            />
          ) : (
            <>
              {/* Summary bar */}
              <RecommendationSummaryBar summary={recommendation_summary} />

              {/* Recommendation tables */}
              <div className="mt-4 space-y-4">
                {recommendation_sections.map((section) => (
                  <RecommendationTable
                    key={section.key}
                    section={section}
                    workspaceSlug={workspace.slug}
                    organizationPrsRemaining={organization_prs_remaining}
                    organizationPlan={organization_plan}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
